import React, { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";

// Lazy-loaded route components for code splitting
const LandingOrDashboard = lazy(() => import("./components/LandingOrDashboard"));
const StockAnalysis = lazy(() => import("./pages/StockAnalysis"));
const StockSearch = lazy(() => import("./pages/StockSearch"));
const Watchlist = lazy(() => import("./pages/Watchlist"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Admin = lazy(() => import("./pages/Admin"));
const Plans = lazy(() => import("./pages/Plans"));
const Profile = lazy(() => import("./pages/Profile"));
const Wallet = lazy(() => import("./pages/Wallet"));
const Billing = lazy(() => import("./pages/Billing"));
const DailyTips = lazy(() => import("./pages/DailyTips"));
const ActiveTips = lazy(() => import("./pages/ActiveTips"));
const ExpiredTips = lazy(() => import("./pages/ExpiredTips"));
const TipsAnalysis = lazy(() => import("./pages/TipsAnalysis"));
const AddTip = lazy(() => import("./pages/AddTip"));
const TradingWallet = lazy(() => import("./pages/TradingWallet"));
const DeletedTips = lazy(() => import("./pages/DeletedTips"));
const MyTrades = lazy(() => import("./pages/MyTrades"));
const AdminTransactions = lazy(() => import("./pages/AdminTransactions"));
const AdminReferrals = lazy(() => import("./pages/AdminReferrals"));
const AdminPlans = lazy(() => import("./pages/AdminPlans"));
const AdminPaymentSettings = lazy(() => import("./pages/AdminPaymentSettings"));
const AdminBuyingPower = lazy(() => import("./pages/AdminBuyingPower"));
const AdminPromotions = lazy(() => import("./pages/AdminPromotions"));
const AdminSubscriptions = lazy(() => import("./pages/AdminSubscriptions"));
const AdminAuditLog = lazy(() => import("./pages/AdminAuditLog"));
const AdminInvoices = lazy(() => import("./pages/AdminInvoices"));
const AdminUserDetail = lazy(() => import("./pages/AdminUserDetail"));
const AdminUserApproval = lazy(() => import("./pages/AdminUserApproval"));
const Checkout = lazy(() => import("./pages/Checkout"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const Ref = lazy(() => import("./pages/Ref"));
const FAQ = lazy(() => import("./pages/FAQ"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const Loader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

/** Wraps children with Layout for authenticated app pages */
const AppPage = ({ children }: { children: React.ReactNode }) => (
  <Layout>
    <ProtectedRoute>{children}</ProtectedRoute>
  </Layout>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Suspense fallback={<Loader />}>
              <Routes>
                {/* Public routes — no Layout wrapper */}
                <Route path="/" element={<LandingOrDashboard />} />
                <Route path="/login" element={<Auth />} />
                <Route path="/signup" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/Ref" element={<Ref />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/terms" element={<TermsAndConditions />} />

                {/* Authenticated routes — wrapped with Layout */}
                <Route path="/stock/:ticker" element={<AppPage><StockAnalysis /></AppPage>} />
                <Route path="/search" element={<AppPage><StockSearch /></AppPage>} />
                <Route path="/watchlist" element={<AppPage><Watchlist /></AppPage>} />
                <Route path="/plans" element={<AppPage><Plans /></AppPage>} />
                <Route path="/admin" element={<AppPage><Admin /></AppPage>} />
                <Route path="/profile" element={<AppPage><Profile /></AppPage>} />
                <Route path="/wallet" element={<AppPage><Wallet /></AppPage>} />
                <Route path="/billing" element={<AppPage><Billing /></AppPage>} />
                <Route path="/daily-tips" element={<AppPage><DailyTips /></AppPage>} />
                <Route path="/active-tips" element={<AppPage><DailyTips /></AppPage>} />
                <Route path="/expired-tips" element={<AppPage><DailyTips /></AppPage>} />
                <Route path="/tips-analysis" element={<AppPage><DailyTips /></AppPage>} />
                <Route path="/deleted-tips" element={<AppPage><DailyTips /></AppPage>} />
                <Route path="/add-tip" element={<AppPage><AddTip /></AppPage>} />
                <Route path="/trading-wallet" element={<AppPage><TradingWallet /></AppPage>} />
                <Route path="/my-trades" element={<AppPage><MyTrades /></AppPage>} />
                <Route path="/admin/transactions" element={<AppPage><AdminTransactions /></AppPage>} />
                <Route path="/admin/referrals" element={<AppPage><AdminReferrals /></AppPage>} />
                <Route path="/admin/plans" element={<AppPage><AdminPlans /></AppPage>} />
                <Route path="/admin/payment-settings" element={<AppPage><AdminPaymentSettings /></AppPage>} />
                <Route path="/admin/buying-power" element={<AppPage><AdminBuyingPower /></AppPage>} />
                <Route path="/admin/promotions" element={<AppPage><AdminPromotions /></AppPage>} />
                <Route path="/admin/subscriptions" element={<AppPage><AdminSubscriptions /></AppPage>} />
                <Route path="/admin/audit-log" element={<AppPage><AdminAuditLog /></AppPage>} />
                <Route path="/admin/invoices" element={<AppPage><AdminInvoices /></AppPage>} />
                <Route path="/admin/user/:userId" element={<AppPage><AdminUserDetail /></AppPage>} />
                <Route path="/admin/user-approval" element={<AppPage><AdminUserApproval /></AppPage>} />
                <Route path="/checkout" element={<AppPage><Checkout /></AppPage>} />
                <Route path="/payment-success" element={<AppPage><PaymentSuccess /></AppPage>} />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
